import { ApiError, createApiClient, fetchWhoAmI, type WhoAmI } from "@kontextwerk/web-common";

import { getToken } from "./auth";
import { notifyUnauthorized } from "./authGuard";
import type {
  CatalogProvider,
  ClientAssets,
  ConsentRecord,
  ConsentRequirement,
  DeploymentConfig,
  EmailChannel,
  EmbeddingBinding,
  GraphStats,
  HealthScore,
  IntegrationType,
  LlmBinding,
  MemberList,
  MyBearerToken,
  MyKey,
  Organization,
  Realm,
  RealmConfigResponse,
  RealmList,
  RealmRenderProfileSet,
  RenderProfileBinding,
  SecretNames,
  ServiceAccount,
  SourceAdapter,
  VersionInfo,
} from "./types";

export { ApiError } from "@kontextwerk/web-common";

const BASE_URL = "/core/v1";

const call = createApiClient({
  baseUrl: BASE_URL,
  getToken,
  onUnauthorized: notifyUnauthorized,
});

/**
 * Deliberately without ``onUnauthorized`` — this client backs the session
 * re-probe, and routing its own 401 back into the guard would recurse.
 */
const probeCall = createApiClient({ baseUrl: BASE_URL, getToken });

/** Realm ids carry ``:`` (and personal realms ``@``/``.``) — always encode. */
const rp = encodeURIComponent;

/**
 * One writer so no call site hand-rolls headers: the shared client sets
 * ``Accept`` but never ``Content-Type``.
 */
function write<T>(
  path: string,
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  body?: Record<string, unknown>,
): Promise<T> {
  return call<T>(path, {
    method,
    ...(body === undefined
      ? {}
      : { headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }),
  });
}

/** Swallows only "not visible to me" (403/404); every other failure propagates. */
async function orNull<T>(fetcher: () => Promise<T>): Promise<T | null> {
  try {
    return await fetcher();
  } catch (e) {
    if (e instanceof ApiError && (e.status === 403 || e.status === 404)) return null;
    throw e;
  }
}

// --- identity ----------------------------------------------------------------

export async function fetchVersion(): Promise<VersionInfo> {
  return call<VersionInfo>("/version", {}, { auth: false });
}

export async function fetchMe(): Promise<WhoAmI> {
  return fetchWhoAmI(call);
}

export type IdentityProbe =
  | { state: "ok"; me: WhoAmI }
  | { state: "unauthorized" }
  /** Network error or 5xx — says nothing about the session, so never sign out. */
  | { state: "unknown"; message: string };

export async function probeIdentity(): Promise<IdentityProbe> {
  try {
    return { state: "ok", me: await fetchWhoAmI(probeCall) };
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return { state: "unauthorized" };
    return { state: "unknown", message: e instanceof Error ? e.message : String(e) };
  }
}

// --- realms: READ via /me (per-user scoped), WRITE via /admin ----------------
// GET /v1/admin/realms is the operator-wide list; /v1/me/realms is the caller's
// view and widens correctly for deployment-wide scopes.

export async function listMyRealms(): Promise<Realm[]> {
  const data = await call<RealmList>("/me/realms");
  return data.realms;
}

/** Full ``RealmResponse`` — serves the detail header without an admin-list call. */
export async function fetchMyRealm(id: string): Promise<Realm> {
  return call<Realm>(`/me/realms/${rp(id)}/pack-bindings`);
}

export async function fetchMyRealmConfig(id: string): Promise<RealmConfigResponse> {
  return call<RealmConfigResponse>(`/me/realms/${rp(id)}/config`);
}

/**
 * Der Instanz-Katalog der anbindbaren Fremdsysteme (D-GOV-24).
 *
 * ``kw:read``-Floor statt ``kw:realm-admin`` — deshalb sieht auch ein reiner
 * Realm-Owner, welche Typen es gibt. Ohne ``include_schema``: die sechs
 * JSON-Schemata vervielfachen die Antwort und werden hier nicht gebraucht.
 */
export async function fetchIntegrationCatalog(): Promise<IntegrationType[]> {
  const data = await call<{ integrations: IntegrationType[] }>("/integrations");
  return data.integrations;
}

/** Die gebundenen Quellen — Owner-Fläche, kein Operator-Bearer nötig. */
export async function fetchMyRealmSourceAdapters(id: string): Promise<SourceAdapter[]> {
  const data = await call<{ adapters: SourceAdapter[] }>(`/me/realms/${rp(id)}/source-adapters`);
  return data.adapters;
}

/**
 * Der Email-Kanal — Owner-Fläche. ``/v1/me`` ist hier das weitere Gate: es lässt
 * neben dem Owner auch platform-, org- und realm-admin durch, ``/v1/admin``
 * verlangt ``kw:realm-admin``. Ein 403 heißt also: weder Owner noch Admin dieses
 * Realms — und wird in der Ansicht benannt statt verschluckt (D-GOV-25).
 */
export async function fetchRealmEmailChannel(id: string): Promise<EmailChannel> {
  return call<EmailChannel>(`/me/realms/${rp(id)}/channel/email`);
}

export async function fetchRealmMembers(id: string): Promise<MemberList> {
  return call<MemberList>(`/admin/realms/${rp(id)}/members`);
}

export async function fetchRealmGraphStats(id: string): Promise<GraphStats> {
  // Resolved via resolve_effective_realm ⇒ needs the active-realm header.
  return call<GraphStats>(`/realms/${rp(id)}/graph/stats`, {}, { realm: id });
}

// --- cluster (a): provider / embedding / render ------------------------------

export async function fetchLlmCatalog(): Promise<CatalogProvider[]> {
  const data = await call<{ providers: CatalogProvider[] }>("/admin/llm-providers");
  return data.providers;
}

/** Die Provider-Bindung — Owner-Fläche; die Antwort trägt kein Credential-Material. */
export async function fetchRealmLlmBinding(id: string): Promise<LlmBinding | null> {
  return orNull(() => call<LlmBinding>(`/me/realms/${rp(id)}/llm-provider`));
}

export interface SetLlmBindingBody {
  provider_name: string;
  completion_model: string | null;
  inherit_org_credentials: boolean;
  /** Platform-admin only (D-LLM-4); omit entirely for a catalog reference. */
  credentials?: Record<string, string>;
}

export async function setRealmLlmBinding(
  id: string,
  body: SetLlmBindingBody,
): Promise<LlmBinding> {
  return write<LlmBinding>(`/admin/realms/${rp(id)}/llm-provider`, "PUT", { ...body });
}

export async function fetchRealmEmbedding(id: string): Promise<EmbeddingBinding | null> {
  return orNull(() => call<EmbeddingBinding>(`/admin/realms/${rp(id)}/embedding-provider`));
}

export interface SetEmbeddingBody {
  provider_name: string;
  model: string;
  credentials?: Record<string, string>;
}

export async function setRealmEmbedding(
  id: string,
  body: SetEmbeddingBody,
): Promise<EmbeddingBinding> {
  return write<EmbeddingBinding>(`/admin/realms/${rp(id)}/embedding-provider`, "PUT", {
    ...body,
  });
}

export async function deleteRealmEmbedding(id: string): Promise<{ deleted: boolean }> {
  return write<{ deleted: boolean }>(`/admin/realms/${rp(id)}/embedding-provider`, "DELETE");
}

/**
 * The realm-effective profile set (D-RENDER-15) — the union of what the realm's
 * bound packs declare, which is also exactly what the server accepts as a
 * binding. Reading the same set the server validates against is what keeps the
 * picker from offering a value the save would reject.
 */
export async function fetchRealmRenderProfiles(id: string): Promise<RealmRenderProfileSet> {
  return call<RealmRenderProfileSet>(`/realms/${rp(id)}/render-profiles`);
}

/**
 * One anchor image as an object URL. Not routed through `call` (which is
 * JSON-only) and not usable as a bare `<img src>` either — the route requires a
 * Bearer token, which an `<img>` request would not carry. The caller owns the
 * returned URL and must revoke it.
 */
export async function fetchRealmRenderProfileImage(
  realmId: string,
  profileId: string,
  index: number,
): Promise<string> {
  const headers = new Headers({ Accept: "image/*" });
  const tok = getToken();
  if (tok) headers.set("Authorization", `Bearer ${tok}`);
  const resp = await fetch(
    `${BASE_URL}/realms/${rp(realmId)}/render-profiles/${rp(profileId)}/reference-images/${index}`,
    { headers },
  );
  if (resp.status === 401) {
    notifyUnauthorized();
    throw new ApiError(401, "Bearer-Token ungültig oder abgelaufen");
  }
  if (!resp.ok) throw new ApiError(resp.status, await resp.text());
  return URL.createObjectURL(await resp.blob());
}

/** Die Render-Profil-Bindung — Owner-Fläche; Style-Token sind keine Secrets. */
export async function fetchRealmRenderProfile(
  id: string,
): Promise<RenderProfileBinding | null> {
  return orNull(() => call<RenderProfileBinding>(`/me/realms/${rp(id)}/render-profile`));
}

export async function setRealmRenderProfile(
  id: string,
  profileId: string,
): Promise<RenderProfileBinding> {
  return write<RenderProfileBinding>(`/admin/realms/${rp(id)}/render-profile`, "PUT", {
    profile_id: profileId,
  });
}

export async function deleteRealmRenderProfile(id: string): Promise<{ deleted: boolean }> {
  return write<{ deleted: boolean }>(`/admin/realms/${rp(id)}/render-profile`, "DELETE");
}

// --- cluster (e): consents (self-scoped; subject comes from the token) -------

export async function fetchMyConsents(): Promise<ConsentRecord[]> {
  return call<ConsentRecord[]>("/consent");
}

export async function fetchRealmConsentRequirements(
  realmId: string,
): Promise<ConsentRequirement[]> {
  const assets = await call<ClientAssets>(`/realms/${rp(realmId)}/client-assets`);
  return assets.consent_requirements ?? [];
}

export async function grantConsent(req: ConsentRequirement): Promise<ConsentRecord> {
  return write<ConsentRecord>("/consent", "POST", {
    pack_id: req.pack_id,
    purpose: req.purpose,
    // Pass through, never compute: the server owns this and ignores it today.
    version_hash: req.version_hash,
  });
}

export async function revokeConsent(consentId: string): Promise<ConsentRecord> {
  return write<ConsentRecord>(`/consent/${rp(consentId)}/revoke`, "POST");
}

// --- deployment config (read-only) ------------------------------------------

export async function fetchDeploymentConfig(prefix?: string): Promise<DeploymentConfig> {
  const query = prefix ? `?prefix=${encodeURIComponent(prefix)}` : "";
  return call<DeploymentConfig>(`/admin/config${query}`);
}

// --- operator-wide reads (static-operator-bearer surfaces) ------------------

export async function fetchHealthScore(): Promise<HealthScore> {
  return call<HealthScore>("/admin/health-score");
}

export async function fetchOrganizations(): Promise<Organization[]> {
  return call<Organization[]>("/admin/organizations");
}

export async function fetchServiceAccounts(): Promise<ServiceAccount[]> {
  return call<ServiceAccount[]>("/admin/service-accounts");
}

// --- realm secrets ----------------------------------------------------------

/** Die Secret-Namen eines Realms — Owner-Fläche, kein Operator-Bearer nötig. */
export async function fetchRealmSecretNames(id: string): Promise<string[]> {
  const data = await call<SecretNames>(`/me/realms/${rp(id)}/secrets`);
  return data.names;
}

/**
 * Write-only: the server encrypts (per-realm derived Fernet) and replaces the
 * stored ciphertext. The value is never fetched or echoed back (D-GOV-21).
 */
export async function putRealmSecret(
  id: string,
  name: string,
  value: Record<string, string>,
): Promise<{ name: string; stored: boolean }> {
  return write<{ name: string; stored: boolean }>(
    `/admin/realms/${rp(id)}/secrets/${rp(name)}`,
    "PUT",
    { value },
  );
}

// --- /me self-service -------------------------------------------------------

export async function fetchMyKeys(): Promise<MyKey[]> {
  const data = await call<{ keys: MyKey[] }>("/me/keys");
  return data.keys;
}

export async function fetchMyTokens(): Promise<MyBearerToken[]> {
  const data = await call<{ tokens: MyBearerToken[] }>("/me/tokens");
  return data.tokens;
}
